import type { MouseEvent } from 'react';
import React, { useCallback } from 'react';
import type { ToolProps } from './Tool';
import { Tool } from './Tool';
import type { ClassNameProp } from '../common-types';
import type { ValidIconName } from './Icon';

/**
 * Properties for {@link FocusTool} component.
 */
export type FocusToolProps = ClassNameProp &
  Required<Pick<ToolProps, 'tooltip'>> & {
    /**
     * Item of {@link ValidIconName}, default: skip-link-right.
     */
    icon?: ValidIconName;
    /**
     * Query selector expression or function that will return {@link HTMLElement}.
     */
    selector: string | ((event: MouseEvent) => HTMLElement);
  };

/**
 * Changes focus context to another {@link HTMLElement}.
 *
 * @param className additional classname for design purposes
 * @param icon name of {@link ValidIconName} for tool visualization
 * @param selector query selector or function that returns {@link HTMLElement}
 * @param tooltip description of change focus action for user
 * @param props leftover props
 *
 * @constructor
 */
export function FocusTool({
  className = '',
  icon = 'skip-link-right',
  selector,
  tooltip,
  ...props
}: Readonly<FocusToolProps>): JSX.Element {
  const focusTo = useCallback(
    (event: MouseEvent) => {
      if (typeof selector === 'string') {
        (document.querySelector(selector) as HTMLElement)?.focus();
        return;
      }
      selector(event)?.focus();
    },
    [selector]
  );

  return (
    <Tool
      {...props}
      className={['ehs-focus-to', className].filter(Boolean).join(' ')}
      icon={icon}
      onClick={focusTo}
      tooltip={tooltip}
    />
  );
}
